import { extractLocationNumericId } from './availability';

const STORES_NEAR_ME_QUERY = `
  query StoresNearMe($near: GeoCoordinateInput!, $first: Int!) {
    locations(first: $first, near: $near, sortKey: DISTANCE) {
      edges {
        node {
          id
          name
          address {
            formatted
            latitude
            longitude
            phone
          }
        }
      }
    }
  }
`;

const ALL_PICKUP_LOCATIONS_QUERY = `
  query AllPickupLocations($first: Int!) {
    locations(first: $first, sortKey: NAME) {
      edges {
        node {
          id
          name
          address {
            formatted
            latitude
            longitude
            phone
          }
        }
      }
    }
  }
`;

const CART_VARIANT_AVAILABILITY_QUERY = `
  query CartVariantAvailability($ids: [ID!]!, $locationId: ID!) @inContext(preferredLocationId: $locationId) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        storeAvailability(first: 1) {
          edges {
            node {
              quantityAvailable
              location {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const VARIANT_AVAILABILITY_QUERY = `
  query VariantAvailability($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        quantityAvailable
        storeAvailability(first: 50) {
          edges {
            node {
              quantityAvailable
              pickUpTime
              location {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const LOCATION_FETCH_LIMIT = 50;

function getStoreLocatorConfig() {
  return window.theme?.settings?.storeLocator ?? {};
}

function formatAddress(address) {
  if (!address?.formatted) return '';
  return Array.isArray(address.formatted)
    ? address.formatted.join(', ')
    : String(address.formatted);
}

function normalizeLocation(node) {
  return {
    id: node.id,
    locationId: extractLocationNumericId(node.id),
    name: node.name,
    address: formatAddress(node.address),
    phone: node.address?.phone ?? '',
    latitude: node.address?.latitude ?? null,
    longitude: node.address?.longitude ?? null,
  };
}

const STOREFRONT_FETCH_TIMEOUT_MS = 8000;

async function storefrontGraphql(query, variables) {
  const { storefrontAccessToken, shopDomain, apiVersion } = getStoreLocatorConfig();

  if (!storefrontAccessToken) {
    throw new Error('Storefront API access token is not configured.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), STOREFRONT_FETCH_TIMEOUT_MS);

  let response;

  try {
    response = await fetch(
      `https://${shopDomain}/api/${apiVersion}/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        },
        body: JSON.stringify({ query, variables }),
        signal: controller.signal,
      }
    );
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Storefront API request timed out.');
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(`Storefront API request failed (${response.status})`);
  }

  const payload = await response.json();

  if (payload.errors?.length) {
    throw new Error(payload.errors.map((error) => error.message).join(', '));
  }

  return payload.data;
}

export async function fetchLocationsNear(coordinates) {
  const data = await storefrontGraphql(STORES_NEAR_ME_QUERY, {
    near: {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    },
    first: LOCATION_FETCH_LIMIT,
  });

  return (data?.locations?.edges ?? []).map(({ node }) => normalizeLocation(node));
}

export async function fetchAllPickupLocations() {
  const data = await storefrontGraphql(ALL_PICKUP_LOCATIONS_QUERY, {
    first: LOCATION_FETCH_LIMIT,
  });

  return (data?.locations?.edges ?? []).map(({ node }) => normalizeLocation(node));
}

export async function fetchCartVariantsAvailability(variantGids, locationGid) {
  const data = await storefrontGraphql(CART_VARIANT_AVAILABILITY_QUERY, {
    ids: variantGids,
    locationId: locationGid,
  });

  const availabilityByVariantGid = new Map();

  (data?.nodes ?? []).forEach((node) => {
    if (!node?.id) return;

    const storeAvailability = node.storeAvailability?.edges?.[0]?.node;
    const isAtSelectedLocation = storeAvailability?.location?.id === locationGid;

    const storeQuantity = isAtSelectedLocation
      ? Number(storeAvailability?.quantityAvailable) || 0
      : 0;

    availabilityByVariantGid.set(node.id, {
      available: storeQuantity > 0,
      quantityAvailable: storeQuantity,
    });
  });

  return availabilityByVariantGid;
}

export async function fetchVariantAvailability(variantGid) {
  const data = await storefrontGraphql(VARIANT_AVAILABILITY_QUERY, { id: variantGid });
  const variant = data?.node;

  if (!variant) {
    throw new Error('Variant availability not found.');
  }

  const storeAvailabilities = (variant.storeAvailability?.edges ?? []).map(({ node }) => ({
    locationId: extractLocationNumericId(node.location?.id),
    quantityAvailable: Number(node.quantityAvailable) || 0,
    pickUpTime: node.pickUpTime ?? null,
  }));

  const onlineQuantity = Number(variant.quantityAvailable) || 0;

  return {
    onlineQuantity,
    storeAvailabilities,
  };
}
